import { Navigate, useRouter, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useMe } from '../../shared/auth.js';
import { Loading } from '../ui/feedback.js';
import { GP, garagePath, landingPath, usableMemberships } from '../lib/paths.js';

/** Adresse racine `/` : connexion, ou l'espace où l'utilisateur travaille (vague 5). */
export default function RootPage() {
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (!me.data) return <Navigate to="/login" replace />;
  return <Navigate to={landingPath(me.data)} replace />;
}

/** Ancienne adresse (avant la vague 5) → même page sous `/g/<slug>/…`. */
const LEGACY: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^\/parc\/?$/, () => GP.parc],
  [/^\/dossiers\/?$/, () => GP.dossiers],
  [/^\/entree\/?$/, () => GP.entree],
  [/^\/equipe\/?$/, () => GP.compteSection('equipe')],
  [/^\/reglages\/?$/, () => GP.compte],
  [/^\/interventions\/([0-9a-f-]{36})\/?$/i, (m) => GP.intervention(m[1] ?? '')],
  [/^\/vehicules\/([0-9a-f-]{36})\/?$/i, (m) => GP.vehicle(m[1] ?? '')],
];

export function legacyTarget(pathname: string): string | null {
  for (const [re, to] of LEGACY) {
    const m = pathname.match(re);
    if (m) return to(m);
  }
  return null;
}

/**
 * Redirige une ancienne adresse vers le garage actif de la session (filtres de l'adresse conservés).
 * Sans garage actif : écran de choix.
 */
export function LegacyRedirect() {
  const me = useMe();
  const router = useRouter();
  const { pathname, searchStr } = useRouterState({ select: (s) => ({ pathname: s.location.pathname, searchStr: s.location.searchStr }) });
  const data = me.data;
  useEffect(() => {
    if (me.isPending) return;
    if (!data) {
      router.history.replace('/login');
      return;
    }
    const slug = data.active?.garageSlug ?? (usableMemberships(data).length === 1 ? usableMemberships(data)[0]?.garageSlug : undefined);
    const sub = legacyTarget(pathname);
    // L'effet peut se relancer avec la nouvelle adresse avant le démontage : on n'agit que sur une ancienne adresse.
    if (sub === null) return;
    router.history.replace(slug && sub !== null ? `${garagePath(slug, sub)}${searchStr}` : '/choose-garage');
  }, [me.isPending, data, pathname, searchStr, router]);
  return <Loading />;
}
